import { useEffect, useState } from "react";
import { Download, Trash2, Inbox, GraduationCap, Presentation, School, BookCheck } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { http, formatApiError } from "@/lib/api";
import { LEAD_TYPES } from "@/components/LeadForm";
import { RevenuePanel } from "@/components/admin/RevenuePanel";

const STATUSES = { new: "New", contacted: "Contacted", demo_scheduled: "Demo scheduled", won: "Won", lost: "Lost" };
const STATUS_CLS = { new: "bg-[#F3DCC9] text-[#8a4b2a]", contacted: "bg-[#E6DEC3] text-stone-700", demo_scheduled: "bg-[#E3EBE5] text-[#2C4A3E]", won: "bg-[#6B9080] text-white", lost: "bg-stone-200 text-stone-500" };
const OVERVIEW = [["leads_new", "New leads", Inbox], ["leads_total", "Total leads", Inbox], ["students", "Students", GraduationCap],
  ["teachers", "Teachers", Presentation], ["classes", "Classes", School], ["lessons_completed", "Lessons completed", BookCheck]];

const exportCsv = (leads) => {
  const cols = ["created_at", "name", "email", "phone", "organization", "lead_type", "students_count", "status", "message", "notes"];
  const csv = [cols, ...leads.map((l) => cols.map((c) => l[c] ?? ""))].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = "consciouslearn_leads.csv";
  a.click();
};

const LeadRow = ({ l, onChange, onDelete }) => {
  const [notes, setNotes] = useState(l.notes);
  return (
    <div className="rounded-3xl bg-[#F7F3EB] border border-[#E6DEC3] p-5 grid md:grid-cols-12 gap-4" data-testid={`lead-row-${l.id}`}>
      <div className="md:col-span-4">
        <p className="font-display text-lg text-[#2C4A3E]">{l.name}</p>
        <a href={`mailto:${l.email}`} className="text-sm text-[#C87D53]">{l.email}</a>
        {l.phone && <p className="text-xs text-stone-500">{l.phone}</p>}
        <p className="text-xs text-stone-500 mt-1">{new Date(l.created_at).toLocaleString()}</p>
      </div>
      <div className="md:col-span-4 text-sm">
        <p><b className="text-stone-700">{LEAD_TYPES[l.lead_type]}</b>{l.organization && ` · ${l.organization}`}</p>
        {l.students_count && <p className="text-stone-500">{l.students_count} students</p>}
        {l.message && <p className="text-stone-600 mt-1 italic">"{l.message}"</p>}
      </div>
      <div className="md:col-span-4 space-y-2">
        <div className="flex gap-2">
          <Select value={l.status} onValueChange={(v) => onChange(l.id, { status: v })}>
            <SelectTrigger className={`rounded-full h-9 border-0 ${STATUS_CLS[l.status]}`} data-testid={`lead-status-${l.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(STATUSES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
          <button onClick={() => onDelete(l.id)} className="p-2 text-stone-400 hover:text-red-600" data-testid={`lead-delete-${l.id}`}><Trash2 className="w-4 h-4" /></button>
        </div>
        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={() => notes !== l.notes && onChange(l.id, { notes })}
          placeholder="Follow-up notes…" data-testid={`lead-notes-${l.id}`}
          className="w-full px-3 py-2 rounded-2xl bg-white border border-[#E6DEC3] text-sm focus:outline-none focus:border-[#2C4A3E]" />
      </div>
    </div>
  );
};

export default function AdminLeads() {
  const [leads, setLeads] = useState(null);
  const [overview, setOverview] = useState(null);
  const [filter, setFilter] = useState("all");
  const load = () => {
    http.get("/admin/leads").then((r) => setLeads(r.data));
    http.get("/admin/overview").then((r) => setOverview(r.data));
  };
  useEffect(load, []);

  const change = async (id, body) => {
    try { await http.patch(`/admin/leads/${id}`, body); toast.success("Lead updated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try { await http.delete(`/admin/leads/${id}`); toast.success("Lead deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const shown = (leads || []).filter((l) => filter === "all" || l.status === filter);

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12" data-testid="admin-leads-page">
      <p className="text-xs uppercase tracking-[0.25em] text-[#C87D53] font-semibold">Owner console</p>
      <h1 className="font-display text-4xl sm:text-5xl text-[#2C4A3E] mt-2">Schools & families</h1>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-10">
        {OVERVIEW.map(([k, label, Icon]) => (
          <div key={k} className="rounded-2xl bg-white/60 border border-[#E6DEC3] p-4">
            <Icon className="w-4 h-4 text-[#C87D53]" />
            <div className="font-display text-2xl text-[#2C4A3E] mt-2" data-testid={`admin-stat-${k}`}>{overview ? overview[k] : "–"}</div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-1">{label}</div>
          </div>
        ))}
      </div>
      <RevenuePanel />
      <div className="flex flex-wrap items-center justify-between gap-3 mt-12">
        <div className="flex flex-wrap gap-2" data-testid="lead-filters">
          {[["all", "All"], ...Object.entries(STATUSES)].map(([k, v]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`lead-filter-${k}`}
              className={`px-4 py-1.5 rounded-full text-sm transition-colors ${filter === k ? "bg-[#2C4A3E] text-white" : "bg-[#F1EADB] text-stone-600"}`}>{v}</button>
          ))}
        </div>
        <button onClick={() => exportCsv(shown)} disabled={!shown.length} data-testid="leads-export-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#E6DEC3] text-sm hover:bg-[#F1EADB] disabled:opacity-50 transition-colors">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>
      <div className="space-y-3 mt-6" data-testid="lead-list">
        {shown.map((l) => <LeadRow key={`${l.id}-${l.updated_at}`} l={l} onChange={change} onDelete={del} />)}
        {leads && !shown.length && <p className="text-center text-stone-500 py-16" data-testid="lead-list-empty">No leads here yet.</p>}
      </div>
    </div>
  );
}
